/**
 * Express API Helper Functions
 * Backend API çağrıları için axios kullanır
 */

import apiClient from './apiClient';

/**
 * Companies API
 */
export const companiesAPI = {
  // Tüm şirketleri getir
  getAll: async () => {
    try {
      const response = await apiClient.get('/companies');
      return response.data || [];
    } catch (error) {
      console.error('❌ Companies API hatası:', error);
      throw error;
    }
  },

  // Tek şirket getir
  getById: async (id) => {
    const response = await apiClient.get(`/companies/${id}`);
    return response.data;
  },

  // Şirket oluştur
  create: async (companyData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = companyData;
    const response = await apiClient.post('/companies', dataWithoutId);
    return response.data;
  },

  // Şirket güncelle
  update: async (id, companyData) => {
    const { id: _, created_at, updated_at, ...cleanData } = companyData;
    
    if (!cleanData || Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/companies/${id}`, cleanData);
    return response.data;
  },

  // Şirket sil
  delete: async (id) => {
    await apiClient.delete(`/companies/${id}`);
    return { success: true };
  }
};

/**
 * Customers API
 */
export const customersAPI = {
  // Tüm müşterileri getir (şirket bilgileri ile)
  getAll: async () => {
    try {
      const response = await apiClient.get('/customers');
      return response.data || [];
    } catch (error) {
      console.error('❌ Customers API hatası:', error);
      throw error;
    }
  },

  // Tek müşteri getir
  getById: async (id) => {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  // Müşteri oluştur
  create: async (customerData) => {
    const { id, created_at, updated_at, companies, company_names, ...customerInfo } = customerData;
    const response = await apiClient.post('/customers', customerInfo);
    return response.data;
  },

  // Müşteri güncelle
  update: async (id, customerData) => {
    const { 
      id: _, 
      created_at, 
      updated_at, 
      companies,
      company_names,
      ...customerInfo 
    } = customerData;
    
    if (Object.keys(customerInfo).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/customers/${id}`, customerInfo);
    return response.data.data;
  },

  // Müşteri sil
  delete: async (id) => {
    await apiClient.delete(`/customers/${id}`);
    return { success: true };
  },

  // Müşteri ara
  search: async (query) => {
    const response = await apiClient.get(`/customers/search?q=${encodeURIComponent(query)}`);
    return response.data || [];
  }
};

/**
 * Domains API
 */
export const domainsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/domains');
      return response.data || [];
    } catch (error) {
      console.error('❌ Domains API hatası:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await apiClient.get(`/domains/${id}`);
    return response.data;
  },

  create: async (domainData) => {
    const { id, created_at, updated_at, companies, company_name, registrar_name, server_name, ...dataWithoutId } = domainData;
    const response = await apiClient.post('/domains', dataWithoutId);
    return response.data;
  },

  update: async (id, domainData) => {
    const { id: _, created_at, updated_at, companies, company_name, registrar_name, server_name, ...cleanData } = domainData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/domains/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/domains/${id}`);
    return { success: true };
  }
};

/**
 * Servers API
 */
export const serversAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/servers');
      return response.data || [];
    } catch (error) {
      console.error('❌ Servers API hatası:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await apiClient.get(`/servers/${id}`);
    return response.data;
  },

  create: async (serverData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = serverData;
    const response = await apiClient.post('/servers', dataWithoutId);
    return response.data;
  },

  update: async (id, serverData) => {
    const { id: _, created_at, updated_at, ...cleanData } = serverData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/servers/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/servers/${id}`);
    return { success: true };
  }
};

/**
 * Registrars API
 */
export const registrarsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/registrars');
      return response.data || [];
    } catch (error) {
      console.error('❌ Registrars API hatası:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await apiClient.get(`/registrars/${id}`);
    return response.data;
  },

  create: async (registrarData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = registrarData;
    const response = await apiClient.post('/registrars', dataWithoutId);
    return response.data;
  },

  update: async (id, registrarData) => {
    const { id: _, created_at, updated_at, ...cleanData } = registrarData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/registrars/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/registrars/${id}`);
    return { success: true };
  }
};

/**
 * Company Payments API
 */
export const companyPaymentsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/company-payments');
      return response.data || [];
    } catch (error) {
      console.error('❌ Company Payments API hatası:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await apiClient.get(`/company-payments/${id}`);
    return response.data;
  },

  getByCompanyId: async (companyId) => {
    const response = await apiClient.get(`/company-payments/company/${companyId}`);
    return response.data || [];
  },

  create: async (paymentData) => {
    const { id, created_at, updated_at, companies, company_name, domains, domain_name, ...dataWithoutId } = paymentData;
    const response = await apiClient.post('/company-payments', dataWithoutId);
    return response.data;
  },

  update: async (id, paymentData) => {
    const { id: _, created_at, updated_at, companies, company_name, domains, domain_name, ...cleanData } = paymentData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/company-payments/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/company-payments/${id}`);
    return { success: true };
  }
};

/**
 * Settings API
 */
export const settingsAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/settings');
      return response.data || {};
    } catch (error) {
      console.error('❌ Settings API hatası:', error);
      throw error;
    }
  },

  get: async (key) => {
    const response = await apiClient.get(`/settings/${key}`);
    return response.data;
  },

  set: async (key, value) => {
    const response = await apiClient.post(`/settings/${key}`, { value });
    return response.data;
  }
};

/**
 * Domain Prices API
 */
export const domainPricesAPI = {
  getAll: async () => {
    const response = await apiClient.get('/domain-prices');
    return response.data || [];
  },

  getById: async (id) => {
    const response = await apiClient.get(`/domain-prices/${id}`);
    return response.data;
  },

  getByExtension: async (extension) => {
    const response = await apiClient.get(`/domain-prices/extension/${extension}`);
    return response.data;
  },

  create: async (priceData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = priceData;
    const response = await apiClient.post('/domain-prices', dataWithoutId);
    return response.data;
  },

  update: async (id, priceData) => {
    const { id: _, created_at, updated_at, ...cleanData } = priceData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/domain-prices/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/domain-prices/${id}`);
    return { success: true };
  }
};

/**
 * Hosting Prices API
 */
export const hostingPricesAPI = {
  getAll: async () => {
    try {
      const response = await apiClient.get('/hosting-prices');
      return response.data || [];
    } catch (error) {
      console.error('❌ Hosting Prices API hatası:', error);
      throw error;
    }
  },

  getById: async (id) => {
    const response = await apiClient.get(`/hosting-prices/${id}`);
    return response.data;
  },

  getByName: async (name) => {
    const response = await apiClient.get(`/hosting-prices/name/${encodeURIComponent(name)}`);
    return response.data;
  },

  create: async (priceData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = priceData;
    const response = await apiClient.post('/hosting-prices', dataWithoutId);
    return response.data;
  },

  update: async (id, priceData) => {
    const { id: _, created_at, updated_at, ...cleanData } = priceData;
    
    if (Object.keys(cleanData).length === 0) {
      throw new Error('Güncelleme verisi boş olamaz');
    }
    
    const response = await apiClient.put(`/hosting-prices/${id}`, cleanData);
    return response.data.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/hosting-prices/${id}`);
    return { success: true };
  }
};

/**
 * Users API
 */
export const usersAPI = {
  getAll: async () => {
    const response = await apiClient.get('/users');
    return response.data || [];
  },

  getById: async (id) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  create: async (userData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = userData;
    const response = await apiClient.post('/users', dataWithoutId);
    return response.data;
  },

  update: async (id, userData) => {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/users/${id}`);
    return { success: true };
  }
};

/**
 * Payments API
 */
export const paymentsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/payments');
    return response.data || [];
  },

  getById: async (id) => {
    const response = await apiClient.get(`/payments/${id}`);
    return response.data;
  },

  create: async (paymentData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = paymentData;
    const response = await apiClient.post('/payments', dataWithoutId);
    return response.data;
  },

  update: async (id, paymentData) => {
    const response = await apiClient.put(`/payments/${id}`, paymentData);
    return response.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/payments/${id}`);
    return { success: true };
  }
};

/**
 * Accounts API - Hesap Yönetimi
 */
export const accountsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/accounts');
    return response.data || [];
  },

  getById: async (id) => {
    const response = await apiClient.get(`/accounts/${id}`);
    return response.data;
  },

  create: async (accountData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = accountData;
    const response = await apiClient.post('/accounts', {
      ...dataWithoutId,
      balance: dataWithoutId.balance || 0,
      is_active: dataWithoutId.is_active !== undefined ? dataWithoutId.is_active : true
    });
    return response.data;
  },

  update: async (id, accountData) => {
    const response = await apiClient.put(`/accounts/${id}`, accountData);
    return response.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/accounts/${id}`);
    return { success: true };
  }
};

/**
 * Mobile Access Keys API - Mobil Erişim Anahtarları
 */
export const mobileAccessKeysAPI = {
  // Tüm erişim anahtarlarını getir
  getAll: async () => {
    const response = await apiClient.get('/mobile-access-keys');
    return response.data || [];
  },

  // Tek erişim anahtarı getir
  getById: async (id) => {
    const response = await apiClient.get(`/mobile-access-keys/${id}`);
    return response.data;
  },

  // Erişim anahtarı oluştur
  create: async (keyData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = keyData;
    const response = await apiClient.post('/mobile-access-keys', dataWithoutId);
    return response.data;
  },

  // Erişim anahtarı güncelle
  update: async (id, keyData) => {
    const { id: _, created_at, updated_at, ...cleanData } = keyData;
    const response = await apiClient.put(`/mobile-access-keys/${id}`, cleanData);
    return response.data.data;
  },

  // Erişim anahtarı sil
  delete: async (id) => {
    await apiClient.delete(`/mobile-access-keys/${id}`);
    return { success: true };
  }
};

/**
 * Account Transactions API - Hesap İşlemleri
 */
export const accountTransactionsAPI = {
  getAll: async (accountId = null) => {
    let url = '/account-transactions';
    if (accountId) {
      url += `?accountId=${accountId}`;
    }
    const response = await apiClient.get(url);
    return response.data || [];
  },

  getById: async (id) => {
    const response = await apiClient.get(`/account-transactions/${id}`);
    return response.data;
  },

  create: async (transactionData) => {
    const response = await apiClient.post('/account-transactions', {
      ...transactionData,
      amount: parseFloat(transactionData.amount) || 0
    });
    return response.data;
  },

  update: async (id, transactionData) => {
    const response = await apiClient.put(`/account-transactions/${id}`, {
      ...transactionData,
      amount: parseFloat(transactionData.amount) || 0
    });
    return response.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/account-transactions/${id}`);
    return { success: true };
  },

  // Para birimi çevirme işlemi
  convertCurrency: async (fromAccountId, toAccountId, amount, exchangeRate) => {
    const response = await apiClient.post('/account-transactions/convert', {
      fromAccountId,
      toAccountId,
      amount,
      exchangeRate
    });
    return response;
  }
};

/**
 * Languages API
 */
export const languagesAPI = {
  // Tüm dilleri getir
  getAll: async () => {
    try {
      const response = await apiClient.get('/languages');
      return response.data || [];
    } catch (error) {
      console.error('❌ Languages API hatası:', error);
      throw error;
    }
  },

  // Aktif dilleri getir
  getActive: async () => {
    try {
      const response = await apiClient.get('/languages/active');
      return response.data || [];
    } catch (error) {
      console.error('❌ Languages getActive hatası:', error);
      throw error;
    }
  },

  // Varsayılan dili getir
  getDefault: async () => {
    try {
      const response = await apiClient.get('/languages/default');
      return response.data;
    } catch (error) {
      console.error('❌ Languages getDefault hatası:', error);
      throw error;
    }
  },

  // Tek dil getir
  getById: async (id) => {
    const response = await apiClient.get(`/languages/${id}`);
    return response.data;
  },

  // Dil kodu ile getir
  getByCode: async (code) => {
    const response = await apiClient.get(`/languages/code/${code}`);
    return response.data;
  },

  // Dil oluştur
  create: async (languageData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = languageData;
    const response = await apiClient.post('/languages', dataWithoutId);
    return response.data;
  },

  // Dil güncelle
  update: async (id, languageData) => {
    const { id: _, created_at, updated_at, ...cleanData } = languageData;
    const response = await apiClient.put(`/languages/${id}`, cleanData);
    return response.data.data;
  },

  // Dil sil
  delete: async (id) => {
    await apiClient.delete(`/languages/${id}`);
    return { success: true };
  }
};

/**
 * Translations API
 */
export const translationsAPI = {
  // Tüm çevirileri getir
  getAll: async () => {
    try {
      const response = await apiClient.get('/translations');
      return response.data || [];
    } catch (error) {
      console.error('❌ Translations getAll hatası:', error);
      throw error;
    }
  },

  // Dile göre çevirileri getir
  getByLanguage: async (languageCode) => {
    try {
      const response = await apiClient.get(`/translations/language/${languageCode}`);
      return response.data || {};
    } catch (error) {
      console.error('❌ Translations getByLanguage hatası:', error);
      throw error;
    }
  },

  // Kategoriye göre çevirileri getir
  getByCategory: async (category) => {
    try {
      const response = await apiClient.get(`/translations/category/${category}`);
      return response.data || [];
    } catch (error) {
      console.error('❌ Translations getByCategory hatası:', error);
      throw error;
    }
  },

  // Tek çeviri getir
  getById: async (id) => {
    const response = await apiClient.get(`/translations/${id}`);
    return response.data;
  },

  // Çeviri oluştur
  create: async (translationData) => {
    const { id, created_at, updated_at, ...dataWithoutId } = translationData;
    const response = await apiClient.post('/translations', dataWithoutId);
    return response.data;
  },

  // Toplu çeviri oluştur
  createBulk: async (translationsArray) => {
    const cleanData = translationsArray.map(({ id, created_at, updated_at, ...rest }) => rest);
    const response = await apiClient.post('/translations/bulk', { translations: cleanData });
    return response.data;
  },

  // Çeviri güncelle
  update: async (id, translationData) => {
    const { id: _, created_at, updated_at, language, ...cleanData } = translationData;
    const response = await apiClient.put(`/translations/${id}`, cleanData);
    return response.data.data;
  },

  // Çeviri sil
  delete: async (id) => {
    await apiClient.delete(`/translations/${id}`);
    return { success: true };
  },

  // Dile ait tüm çevirileri sil
  deleteByLanguage: async (languageId) => {
    await apiClient.delete(`/translations/language/${languageId}`);
    return { success: true };
  },

  // Çeviri anahtarını ara
  search: async (searchTerm) => {
    const response = await apiClient.get(`/translations/search?q=${encodeURIComponent(searchTerm)}`);
    return response.data || [];
  }
};

export const supportAPI = {
  // Tüm ticketları getir (admin)
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiClient.get(`/support${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  // Müşteri ticketlarını getir
  getCustomerTickets: async (customerId) => {
    const response = await apiClient.get(`/support/customer/${customerId}`);
    return response.data;
  },

  // Tek ticket getir
  getById: async (id) => {
    const response = await apiClient.get(`/support/${id}`);
    return response.data;
  },

  // Ticket oluştur
  create: async (ticketData) => {
    const response = await apiClient.post('/support', ticketData);
    return response.data;
  },

  // Ticket durumunu güncelle
  updateStatus: async (id, status) => {
    const response = await apiClient.patch(`/support/${id}/status`, { status });
    return response.data;
  },

  // Ticket önceliğini güncelle
  updatePriority: async (id, priority) => {
    const response = await apiClient.patch(`/support/${id}/priority`, { priority });
    return response.data;
  },

  // Cevap ekle
  addResponse: async (id, message, isAdmin = false) => {
    const response = await apiClient.post(`/support/${id}/response`, { message, is_admin: isAdmin });
    return response.data;
  },

  // Ticket sil
  delete: async (id) => {
    await apiClient.delete(`/support/${id}`);
    return { success: true };
  },

  // İstatistikleri getir
  getStats: async () => {
    const response = await apiClient.get('/support/stats');
    return response.data;
  }
};
